import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

export default function ReportsPage() {
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/surveys").then((r) => setItems(r.data));
    api.get("/dashboard/overview").then((r) => setOverview(r.data));
  }, []);

  const exportCSV = async (s) => {
    setBusy(s.id);
    try {
      const [respRes, anaRes] = await Promise.all([
        api.get(`/surveys/${s.id}/responses`),
        api.get(`/surveys/${s.id}/analytics`),
      ]);
      const responses = respRes.data;
      const survey = anaRes.data.survey;
      const qids = (survey.questions || []).map((q) => q.id);
      const headers = ["submitted_at", "nps_score", "nps_category", "respondent_email", "respondent_phone", ...qids.map((qid) => {
        const q = survey.questions.find((qq) => qq.id === qid);
        return q ? q.title.replace(/[",\n]/g, " ") : qid;
      })];
      const rows = responses.map((r) => {
        const map = {}; (r.answers || []).forEach((a) => { map[a.question_id] = a.value; });
        return [
          r.submitted_at,
          r.nps_score ?? "",
          r.nps_category ?? "",
          r.respondent_email ?? "",
          r.respondent_phone ?? "",
          ...qids.map((qid) => {
            const v = map[qid];
            if (Array.isArray(v)) return `"${v.join("|")}"`;
            if (typeof v === "string") return `"${v.replace(/"/g, '""')}"`;
            return v ?? "";
          }),
        ].join(",");
      });
      const csv = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${survey.title}-responses.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="grid md:grid-cols-4 gap-4">
        <Kpi label="NPS" value={overview?.totals?.nps_score ?? "—"} tone="brand"/>
        <Kpi label="Surveys" value={overview?.totals?.surveys ?? 0} tone="cyan"/>
        <Kpi label="Published" value={overview?.totals?.published ?? 0} tone="emerald"/>
        <Kpi label="Total responses" value={overview?.totals?.responses ?? 0} tone="rose"/>
      </div>

      <Card className="p-5 border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Reports</p>
            <h2 className="text-lg font-extrabold text-[#1F3A6B]">Survey-level analytics & raw exports</h2>
          </div>
        </div>
      </Card>

      <Card className="border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Survey</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Questions</th>
              <th className="text-left px-4 py-3 font-semibold">Responses</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-slate-500">No surveys yet.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} data-testid={`report-row-${s.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#1F3A6B]"><FileText size={14} className="inline mr-1.5 text-[#F58220]"/>{s.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">/r/{s.public_slug}</p>
                </td>
                <td className="px-4 py-3"><Badge className={s.status === "published" ? "bg-emerald-100 text-emerald-700" : s.status === "archived" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}>{s.status}</Badge></td>
                <td className="px-4 py-3 tabular-nums">{s.questions?.length || 0}</td>
                <td className="px-4 py-3 tabular-nums font-semibold">{s.response_count || 0}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/app/surveys/${s.id}/analytics`}><Button size="sm" variant="outline" className="mr-2"><BarChart3 size={14} className="mr-1.5"/>Dashboard</Button></Link>
                  <Button data-testid={`report-export-${s.id}`} size="sm" onClick={() => exportCSV(s)} disabled={busy === s.id} className="bg-[#F58220] hover:bg-[#E36F0E] text-white">
                    <Download size={14} className="mr-1.5"/>{busy === s.id ? "Exporting…" : "Export CSV"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const map = { brand: "text-[#1F3A6B]", cyan: "text-[#F58220]", emerald: "text-emerald-600", rose: "text-rose-600" };
  return (
    <Card className="p-5 border-slate-100">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`mt-2 text-4xl font-extrabold tabular-nums ${map[tone]}`}>{value}</p>
    </Card>
  );
}
